import * as React from 'react';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { GetAccountNotificationsQuery, useUpdateNotificationsMutation } from '@graphql';
import { NotificationSwitches } from './NotificationSwitches';
import initValues from './initValues';

interface NotificationFormProps {
  notifications: GetAccountNotificationsQuery['getAccountNotifications'];
}

export function NotificationForm(props: Readonly<NotificationFormProps>) {
  const { notifications } = props;
  const debounceTimer = React.useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation('notification');

  const [updateNotifications] = useUpdateNotificationsMutation({
    onCompleted() {
      toast.success(t('toast.success'));
    },
    onError() {
      toast.error(t('toast.error'));
    },
  });

  const formik = useFormik({
    initialValues: initValues(notifications),
    onSubmit(values) {
      // remove empty objects from values
      const payload = Object.fromEntries(
        Object.entries(values).map(([key, value]) => {
          if (Object.keys(value).length === 0) {
            return [key, undefined];
          }
          return [key, value];
        })
      );

      const key = notifications.__typename.replace('Notifications', '').toLowerCase();

      updateNotifications({
        variables: {
          input: {
            [key]: payload,
          },
        },
      });
    },
  });

  // Effect: debounce submit when values change
  React.useEffect(() => {
    if (formik.dirty && formik.isValid) {
      // clear previous timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      // set new debounce for 3 seconds
      debounceTimer.current = setTimeout(() => {
        formik.submitForm();
      }, 2500);
    }

    return () => {
      if (formik.dirty && formik.isValid) {
        // clear timer on every change
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
      }
    };
  }, [formik.values, formik.dirty, formik.isValid, formik.submitForm]);

  // Submit if the form is dirty and valid & component is unmounted
  React.useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    return () => {
      if (formik.dirty && formik.isValid) {
        formik.submitForm();
      }
    };
  }, [formik.dirty, formik.isValid, formik.submitForm]);

  if (!notifications) {
    return null;
  }

  return (
    <Stack spacing={3}>
      <NotificationSwitches type="standby" formik={formik} notifications={notifications} />
      <Divider variant="middle" />
      {Object.keys(formik.values.request).length > 0 && (
        <NotificationSwitches type="request" formik={formik} notifications={notifications} />
      )}
      {Object.keys(formik.values.booking).length > 0 && (
        <NotificationSwitches type="booking" formik={formik} notifications={notifications} />
      )}
      {Object.keys(formik.values.event).length > 0 && (
        <NotificationSwitches type="event" formik={formik} notifications={notifications} />
      )}
      {Object.keys(formik.values.feedback).length > 0 && (
        <NotificationSwitches type="feedback" formik={formik} notifications={notifications} />
      )}
    </Stack>
  );
}
